// Copyright (c) 2020 WiseTime. All rights reserved.

import { from, retry, RetryPolicy, ServerStreamingRpc, UnaryRpc } from "grpc-web-rx"
import { BehaviorSubject, Observable, of, } from "rxjs"
import { refreshExpiredToken } from "./retry"
import { filter, mergeMap } from "rxjs/operators"
import { accessTokenInFlight } from "../../App"

/**
 * Wraps a gRPC-web call and returns and observable of the rpc's return type, it also has a retry policy configured
 * where it will retry requests that fail due to an expired idToken by attempting a token exchange and then retrying
 * the source call (assuming token exchange succeeds).
 *
 * Sample usage:
 *
 * grpcWebExecutor(() => client.callRpc(new Request()))
 *   .subscribe(
 *      response => console.log(`Call success: ${response.id}`),
 *      error => console.error(`Received error status code: ${error.code}`)
 *   )
 */
export const grpc = <T>(
  rpc: ServerStreamingRpc | UnaryRpc<T>,
  retryPolicy: RetryPolicy = refreshExpiredToken(),
  inFlight: BehaviorSubject<boolean> = accessTokenInFlight,
): Observable<T> =>
    of(undefined)
      .pipe(
        // This waits until we have an access token before making the gRPC call.
        filter(_ => !inFlight.value),
        mergeMap(_ =>
          from<T>(rpc)
            .pipe(retry(retryPolicy))
        )
      )
